import {take} from "rxjs/operators";
import {NetscopeApiService, Response, Success} from "@app/services/netscope-api.service";
import {
  EXTERNAL_IP_UUID_PREFIX,
  IP_NETWORK_UUID_PREFIX,
  UNKNOWN_IP_UUID_PREFIX,
  ipv4AddressValue,
  ipv4Network,
  isIpv6Address,
  offsetIpv4Address,
  uuidAddress
} from "@app/netscope/model/ip-addresses";

/**
 * What apps groups are built from: the resources of the infrastructure, their addresses and the configured
 * external IPs. It names the members of a group, which are only stored as uuids, and offers the resources a
 * group can pick, the addresses and networks the user types included.
 */
export class AppsGroupsInventory {
  vms = [];
  // Every resource of the infrastructure, indexed by uuid: the members of an apps group are only stored as
  // uuids, and they are not only VMs — hosts and the other resources of the topology can be grouped too.
  resourcesByUuid: Map<string, any> = new Map();
  // Addresses of every resource of the topology, indexed by uuid: the topology itself only names its
  // resources, their addresses are held by the node mapping, which indexes them the other way around.
  addressesByUuid: Map<string, string[]> = new Map();
  // Addresses of the topology, indexed by address: an address already tied to a resource is not offered as
  // an unknown IP, the flows name it after that resource and a member holding it would match nothing.
  resourceUuidsByAddress: Map<string, string> = new Map();
  // Configured external IPs, indexed by name: a member of an apps group can point to one of them, or to one
  // address inside one of them when it is a network, and neither is part of the topology.
  externalIpsByName: Map<string, any> = new Map();
  // Resources the combobox offers: the VMs and the hosts of the topology, and the configured external IPs.
  // A single IP address is not listed, it is built from the address the user types.
  selectableResources: any[] = [];
  // Kind of every selectable resource ('vm', 'host', 'external network', ...), indexed by uuid: a member of
  // an apps group is only a uuid, and its kind is shown next to its name.
  resourceTypesByUuid: Map<string, string> = new Map();

  /**
   * Loads the latest infrastructure and the configured external IPs. Not getting the external IPs is not
   * fatal: the members pointing to one of them just fall back on their uuid. False when the infrastructure
   * could not be loaded.
   */
  load = async (netscopeService: NetscopeApiService): Promise<boolean> => {
    let infrastructureVersionResponse: Response<any> = await netscopeService.getLastInfrastructureVersion()
      .pipe(take(1)).toPromise();
    if (!(infrastructureVersionResponse instanceof Success)) {
      return false;
    }

    let topology = infrastructureVersionResponse.element.topology;
    let vmOnlyTopology = topology.vm_only_topology;
    this.vms = vmOnlyTopology.vms ?? [];
    this.indexResources(vmOnlyTopology);
    this.indexAddresses(topology.node_mapping);

    let externalIpsResponse: Response<any> = await netscopeService.getExternalIps().pipe(take(1)).toPromise();
    if (externalIpsResponse instanceof Success) {
      this.indexExternalIps(externalIpsResponse.element?.external_ips);
    }

    this.buildSelectableResources(vmOnlyTopology);
    return true;
  }

  /**
   * Indexes by uuid every resource of the infrastructure, whatever its type: an apps group holds VMs as well
   * as hosts, networks or any other resource of the topology, and all of them have to be labelled.
   */
  indexResources = (vmOnlyTopology) => {
    this.resourcesByUuid = new Map();

    for (let resources of Object.values(vmOnlyTopology ?? {})) {
      if (!Array.isArray(resources)) {
        continue;
      }

      for (let resource of resources) {
        if (resource != undefined && resource.uuid != undefined) {
          this.resourcesByUuid.set(resource.uuid, resource);
        }
      }
    }
  }

  /**
   * Indexes the addresses of the resources of the topology, which the node mapping holds the other way around
   * ('192.168.5.5' -> the host it belongs to). A resource can answer on several addresses, and they are all
   * kept: the combobox searches on them, and they are shown next to the name of the resource.
   */
  indexAddresses = (nodeMapping) => {
    this.addressesByUuid = new Map();
    this.resourceUuidsByAddress = new Map();

    for (let [address, resource] of Object.entries(nodeMapping ?? {})) {
      let uuid = resource?.["uuid"];

      if (uuid == undefined || address == undefined || address.trim() === "") {
        continue;
      }

      if (!this.addressesByUuid.has(uuid)) {
        this.addressesByUuid.set(uuid, []);
      }
      this.addressesByUuid.get(uuid).push(address);
      this.resourceUuidsByAddress.set(address, uuid);
    }
  }

  /**
   * Indexes the configured external IPs by name, and adds them to the resources index so that a member
   * pointing to one of them as a whole is labelled by its name, as any other resource. It completes the index
   * built by indexResources, and is therefore always called after it.
   */
  indexExternalIps = (externalIps) => {
    this.externalIpsByName = new Map();

    for (let externalIp of externalIps ?? []) {
      if (externalIp == undefined || externalIp.name == undefined) {
        continue;
      }

      this.externalIpsByName.set(externalIp.name, externalIp);
      this.resourcesByUuid.set(externalIp.uuid ?? `${EXTERNAL_IP_UUID_PREFIX}${externalIp.name}`, externalIp);
    }
  }

  /**
   * Address of a member pointing inside a configured external network: such an address is not part of the
   * topology, and its uuid only holds the name of the network and its offset in it ('vim.ExternalIpaddress:
   * bureau:13'), which says nothing to the user and is worth rebuilding into a real address.
   */
  externalIpAddress = (uuid: string): string => {
    if (uuid == undefined || !uuid.startsWith(EXTERNAL_IP_UUID_PREFIX)) {
      return undefined;
    }

    // The name of an external IP cannot hold a ':', so the offset is the second part and nothing else
    let parts = uuid.substring(EXTERNAL_IP_UUID_PREFIX.length).split(":");
    if (parts.length != 2) {
      return undefined;
    }

    let externalIp = this.externalIpsByName.get(parts[0]);
    if (externalIp == undefined || externalIp.ipAddress == undefined) {
      return undefined;
    }

    let offset = Number.parseInt(parts[1], 10);
    if (Number.isNaN(offset)) {
      return undefined;
    }

    // The offset is counted from the address of the network as it was configured: '192.168.3.0' in '192.168.3.0/24'
    return offsetIpv4Address(externalIp.ipAddress.split("/")[0], offset);
  }

  /**
   * Builds the list of resources the combobox offers. Only the resources that can take part in a flow are
   * listed: the VMs and the hosts of the topology, and the external IPs as they were configured. Networks,
   * dswitches and portgroups are left out, they never show up as an end of a flow.
   */
  buildSelectableResources = (vmOnlyTopology) => {
    let resources = [];

    for (let [topologyKey, resourceType] of [["vms", "vm"], ["hosts", "host"]]) {
      for (let resource of vmOnlyTopology?.[topologyKey] ?? []) {
        if (resource == undefined || resource.uuid == undefined) {
          continue;
        }

        resources.push({
          uuid: resource.uuid,
          name: this.resourceLabel(resource.uuid),
          // A resource is reachable on as many addresses as it has interfaces, and the user may search on
          // any of them: they are all kept, and shown as one hint next to the name of the resource.
          address: (this.addressesByUuid.get(resource.uuid) ?? []).join(", "),
          resourceType: resourceType
        });
      }
    }

    for (let externalIp of this.externalIpsByName.values()) {
      resources.push({
        uuid: externalIp.uuid ?? `${EXTERNAL_IP_UUID_PREFIX}${externalIp.name}`,
        name: externalIp.name,
        address: externalIp.ipAddress,
        resourceType: this.externalIpIsNetwork(externalIp) ? "external network" : "external ip"
      });
    }

    this.selectableResources = resources.sort((a, b) => a.name.localeCompare(b.name));

    this.resourceTypesByUuid = new Map();
    for (let resource of resources) {
      this.resourceTypesByUuid.set(resource.uuid, resource.resourceType);
    }
  }

  /**
   * Clarity badge under which a kind of resource is shown, so that the kinds are told apart at a glance and
   * the same kind always wears the same color, in the combobox as in the members of an apps group.
   */
  resourceBadgeClass = (resourceType: string): string => {
    switch (resourceType) {
      case "apps group":
        return "badge-orange";
      case "host":
        return "badge-blue";
      case "vm":
        return "badge-light-blue";
      // The addresses and the networks share their color, their badge spells out which is which
      case "ip":
      case "external ip":
      case "network":
      case "external network":
        return "badge-purple";
      default:
        return "badge-gray";
    }
  }

  /**
   * Kind of a member of an apps group, as it is shown next to its name: the kind of the resource it points to
   * when it is one of the infrastructure, and 'ip' for a lone address, whether it was named after a configured
   * external network or not.
   */
  memberType = (uuid: string): string => {
    if (this.resourceTypesByUuid.has(uuid)) {
      return this.resourceTypesByUuid.get(uuid);
    }

    if (uuid != undefined && uuid.startsWith(IP_NETWORK_UUID_PREFIX)) {
      return "network";
    }

    if (uuid != undefined
        && (uuid.startsWith(UNKNOWN_IP_UUID_PREFIX) || uuid.startsWith(EXTERNAL_IP_UUID_PREFIX))) {
      return "ip";
    }

    // A member of a resource that left the infrastructure, or that the topology does not offer for selection
    return "resource";
  }

  // Whether a configured external IP stands for a whole network ('192.168.3.0/24') or for a single address
  externalIpIsNetwork = (externalIp): boolean => {
    return (externalIp?.ipAddress ?? "").indexOf("/") !== -1;
  }

  /**
   * Resource standing for what the user typed, so that an address or a whole network can be grouped even when
   * it belongs to no resource of the topology. A network ('192.168.10.0/24') stands for every address falling
   * inside it, and a single address gets the uuid DC Netscope names it with in the flows:
   *
   *  - an address inside a configured external network is an offset in that network,
   *  - an address tied to a resource of the topology is that resource, and nothing is built for it: the
   *    resource is already offered by the combobox, and is what the flows hold,
   *  - any other address is an unknown IP, named after the address itself.
   */
  typedAddressResource = (address: string) => {
    address = (address ?? "").trim();

    if (address.indexOf("/") !== -1) {
      return this.typedNetworkResource(address);
    }

    let externalNetworkResource = this.externalNetworkAddressResource(address);
    if (externalNetworkResource != undefined) {
      return externalNetworkResource;
    }

    // An address of the topology is reached through the resource holding it, which the combobox lists already
    if (this.resourceUuidsByAddress.has(address)) {
      return undefined;
    }

    if (ipv4AddressValue(address) == undefined && !isIpv6Address(address)) {
      return undefined;
    }

    return {
      uuid: `${UNKNOWN_IP_UUID_PREFIX}${address}`,
      name: address,
      address: address,
      resourceType: "ip"
    };
  }

  /**
   * Resource standing for a network the user typed in CIDR notation. A network is not a resource of the flows:
   * it is a member gathering every address that falls inside it, and the view resolves it against the
   * resources it drew. A network that was configured as an external IP is offered as that external IP
   * instead, so that the same network is not grouped under two different names.
   */
  typedNetworkResource = (cidr: string) => {
    if (ipv4Network(cidr) == undefined) {
      return undefined;
    }

    for (let externalIp of this.externalIpsByName.values()) {
      if (externalIp.ipAddress === cidr) {
        return undefined;
      }
    }

    return {
      uuid: `${IP_NETWORK_UUID_PREFIX}${cidr}`,
      name: cidr,
      address: cidr,
      resourceType: "network"
    };
  }

  /**
   * Resource standing for an address the user typed that falls inside a configured external network: such an
   * address is not part of the topology, and its uuid only holds the name of the network and its offset in it.
   */
  externalNetworkAddressResource = (address: string) => {
    let addressValue = ipv4AddressValue(address);

    if (addressValue == undefined) {
      return undefined;
    }

    for (let externalIp of this.externalIpsByName.values()) {
      let network = ipv4Network(externalIp.ipAddress);

      // The address belongs to the network when it shares its prefix, the offset being counted from the
      // address as it was configured, the very way DC Netscope names the resource in the flows
      if (network == undefined || !network.contains(address)) {
        continue;
      }

      return {
        uuid: `${EXTERNAL_IP_UUID_PREFIX}${externalIp.name}:${network.offsetOf(address)}`,
        name: address,
        address: address,
        resourceType: "ip"
      };
    }

    return undefined;
  }

  /**
   * Label under which a member of an apps group is shown: the name of the resource it points to, falling back
   * on its address when it has no name, then on the address carried by its uuid, whether it is an unknown IP
   * or an offset in a configured external network, and finally on its raw uuid when the resource is unknown.
   */
  resourceLabel = (uuid: string): string => {
    let resource = this.resourcesByUuid.get(uuid);

    if (resource != undefined) {
      if (resource.name != undefined && resource.name !== "") {
        return resource.name;
      }
      if (resource.address != undefined && resource.address !== "") {
        return resource.address;
      }
    }

    let address = uuidAddress(uuid);
    if (address != undefined) {
      return address;
    }

    let externalIpAddress = this.externalIpAddress(uuid);
    if (externalIpAddress != undefined) {
      return externalIpAddress;
    }

    return uuid;
  }
}
